import React, { useEffect, useMemo, useState } from "react";
import { useLocale } from "lib/hooks/useLocale";
import InputField from "components/common/forms/InputField";
import useForm, { ValidationSchema, FormField } from "lib/hooks/useForm";
import { getFieldProperties } from "lib/utils/address";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import { useSearchParams } from "react-router-dom";
import { getErrorCode, publishPostMessage } from "lib/utils/helpers";
import { analyticsEvents, constants, eventTypes } from "lib/utils/constants";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { CaptchaWrapper, CaptchaState, CaptchaStatus } from "components/common/CaptchaWrapper";
import { OTPDeliveryType, sendOtpWithPhoneNumber, verifyOtp, resentOtp } from "lib/utils/auth";
import { OTPChannel, OTPVerificationDetails } from "lib/types/user";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import OTPForm from "components/auth/forms/OTPForm";
import { NotePencil, User, At } from "@phosphor-icons/react";
import { useUserContext } from "lib/contexts/UserProvider";
import ShopfloBadge from "components/common/ShopfloBadge";

const UserDetails: React.FC = () => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const name = searchParams.get("name");
  const email = searchParams.get("email");
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const {
    state: { merchant },
  } = useMerchantContext();
  const {
    state: { user },
  } = useUserContext();

  const [isBtnLoading, setIsBtnLoading] = useState<boolean>(false);
  const [showOTPForm, setShowOTPForm] = useState<boolean>(false);
  const [counter, setCounter] = useState<number>(-1);
  const [counterExpired, setCounterExpired] = useState<boolean>(false);
  const [contextId, setContextId] = useState<string>("");
  const [userDetails, setUserDetails] = useState({
    name: name ?? "",
    email: email ?? "",
  });
  const [captchaToken, setCaptchaToken] = useState<CaptchaState>({
    token: null,
    status: CaptchaStatus.LOADING,
  });

  const initialState = showOTPForm
    ? {
        otp: "",
      }
    : {
        name: userDetails.name,
        email: userDetails.email,
      };

  const validationSchema = useMemo(() => {
    if (showOTPForm) {
      return {
        otp: {
          required: t("enter_your_otp"),
          numeric: t("otp_incorrect"),
          length: {
            limit: 4,
            message: t("otp_incorrect"),
          },
          formatters: ["NUMERIC"],
        },
      } as ValidationSchema;
    }

    return {
      name: getFieldProperties({ key: "name", isMandatory: true }),
      email: getFieldProperties({ key: "email", isMandatory: true }),
    } as ValidationSchema;
  }, [t, showOTPForm]);

  const {
    handleSubmit,
    inputProps,
    setValueOf,
    setErrors,
    state: { values, errors, validations },
  } = useForm({ initialState, validationSchema });

  const onDialogFormSubmit = async () => {
    const payload = {
      name: userDetails.name,
      email: userDetails.email,
    };

    setIsBtnLoading(true);
    publishPostMessage(eventTypes.FLO_NAME_EMAIL_FORM_SUBMITTED, payload);
  };

  const validateOtp = async (otp: string) => {
    if (Boolean(otp) && otp?.length !== 4) return;
    setErrors({ otp: { status: false } });
    setIsBtnLoading(true);

    try {
      await verifyOtp(contextId, otp);
      setIsBtnLoading(false);

      // After OTP is verified, call onDialogFormSubmit
      await onDialogFormSubmit();
    } catch (e: any) {
      setIsBtnLoading(false);
      setErrors({ otp: { status: true, message: t("otp_incorrect"), errorCode: getErrorCode(e) } });
    }
  };

  const handleResendOtp = async (channel: OTPChannel) => {
    setIsBtnLoading(true);
    setValueOf("otp", "");
    try {
      await resentOtp(contextId, channel);
      setCounter(30);
      setCounterExpired(false);
    } catch (err) {
      console.error(err);
    }
    setErrors({ otp: { status: false } });
    setIsBtnLoading(false);
  };

  const handleEmailOTP = async () => {
    try {
      // Save name and email before switching to OTP form
      setUserDetails({
        name: values.name ?? "",
        email: values.email ?? "",
      });

      const verificationDetails: OTPVerificationDetails = {
        identifier: values.email ?? "",
        merchantId: merchant?.merchantId ?? "",
        userId: user?.uid,
      };

      const contextMetadata: OTPDeliveryType = {
        context: "AUTH",
      };

      setIsBtnLoading(true);
      const result = await sendOtpWithPhoneNumber(
        verificationDetails,
        contextMetadata,
        captchaToken.token ?? "",
        merchant?.captchaProvider ?? "TURNSTILE",
      );

      if (result) {
        setContextId(result.context_id);
        setCounter(30);
        setCounterExpired(false);
        setShowOTPForm(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsBtnLoading(false);
      setCaptchaToken((prev) => ({ ...prev, token: null }));
    }
  };

  useEffect(() => {
    if (counter === 0) setCounterExpired(true);
    const timer: any = counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
    return () => clearInterval(timer);
  }, [counter]);

  useEffect(() => {
    if (values.otp && values.otp.length === 4) {
      validateOtp(values.otp);
    }
  }, [values.otp]);

  useEffect(() => {
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_SSO_FORM_LOADED,
      eventType: "load",
      metaData: {
        userData: {
          name: "",
          email: "",
          phone: "",
        },
      },
    });

    const handleUserDetails = (event: MessageEvent) => {
      if (event?.data?.type === eventTypes.FLO_NAME_EMAIL_FORM_SUBMITTED_SUCCESS) {
        setIsBtnLoading(false);
        return;
      }

      if (event?.data?.type === eventTypes.FLO_NAME_EMAIL_FORM_SUBMITTED_FAILED) {
        setIsBtnLoading(false);
        return;
      }
    };

    window.addEventListener("message", handleUserDetails);
    return () => {
      window.removeEventListener("message", handleUserDetails);
    };
  }, []);

  return (
    <div className="flex h-full w-full flex-1 flex-col items-center justify-start">
      {showOTPForm ? (
        <>
          <div className="flex w-full flex-col gap-1 px-4 pb-6 pt-1">
            <h2 className="text-text-xl-semibold text-primary-dark">{t("otp_sent_title")}</h2>
            <p className="flex items-center gap-1.5 text-text-sm-medium text-coal-light opacity-70">
              <span>
                {t("otp_sent_to_label")} {userDetails.email}
              </span>
              <button
                type="button"
                onClick={() => setShowOTPForm(false)}
                className="flex-shrink-0"
                aria-label="Edit email">
                <NotePencil className="h-4 w-4 text-coal-light" />
              </button>
            </p>
          </div>
          <OTPForm
            isLoading={isBtnLoading}
            otpLength={4}
            otpValue={values.otp ?? ""}
            handleSubmit={handleSubmit(validateOtp)}
            handleChange={(code: string) => {
              setValueOf("otp", code);
              if (errors?.otp?.status) {
                setErrors({ otp: { status: false } });
              }
            }}
            validateOtp={validateOtp}
            handleResendOtp={handleResendOtp}
            error={errors?.otp?.status || false}
            errorCode={errors?.otp?.errorCode}
            errorMessage={t("otp_incorrect_login_sso")}
            autoFocus={true}
            otpCounter={counter}
            otpCounterExpired={counterExpired}
            emailValidation={true}
            showLoginConsent={false}
            context={"sso"}
          />
        </>
      ) : (
        <>
          <div className="flex w-full flex-col gap-1 px-4 pb-6 pt-1">
            <h2 className="text-text-xl-semibold">{t("sso_link_profile_title")}</h2>
            <p className="text-sm font-normal text-coal-light">{t("sso_link_profile_subtitle")}</p>
          </div>
          <div className="w-full">
            <form
              className="flex flex-col space-y-3 pt-8"
              id="address-form"
              onSubmit={handleSubmit(handleEmailOTP)}>
              <InputField
                type="text"
                label={t("full_name")}
                showLabel={false}
                placeholder="john.doe"
                startIcon={<User className="h-5 w-5 text-coal-light" weight="regular" />}
                filled={Boolean(values.name?.length)}
                autoComplete="name"
                {...inputProps(FormField.name)}
                error={errors.name}
                required={Boolean(validationSchema?.name?.required)}
                showSuccessIcon={validations?.name}
                disabletheme={true}
                customClass="!h-12 !pl-10"
              />

              <InputField
                type="text"
                label={t("email")}
                showLabel={false}
                placeholder="john.doe@xyz.com"
                startIcon={<At className="h-5 w-5 text-coal-light" weight="regular" />}
                {...inputProps(FormField.email)}
                filled={Boolean(values.email?.length)}
                autoComplete="email"
                error={errors.email}
                required={Boolean(validationSchema?.email?.required)}
                showSuccessIcon={validations?.email}
                disabletheme={true}
                customClass="!h-12 !pl-10"
              />

              <div className="!mt-4">
                <PrimaryButton
                  type="submit"
                  id="user_details_submit"
                  buttonText={t("continue")}
                  height="h-13"
                  isCheckout={false}
                  borderRadius="rounded-xl"
                  isLoading={isBtnLoading}
                />
              </div>
            </form>
            <CaptchaWrapper
              context="SSO_VERIFY_EMAIL"
              onTokenChange={setCaptchaToken}
              status={captchaToken.status}
            />
          </div>
        </>
      )}
      <div className="mt-auto flex w-full items-center justify-center pb-3 pt-4">
        <ShopfloBadge />
      </div>
    </div>
  );
};

export default UserDetails;
